import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles, LifeBuoy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { VoiceButton } from "@/components/VoiceButton";
import { ChatMessages } from "@/components/chat/ChatMessages";
import { useChat } from "@/components/chat/useChat";

export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [ticketing, setTicketing] = useState(false);
  const navigate = useNavigate();
  const endRef = useRef();
  const { msgs, busy, send, toTicket } = useChat(open);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const submit = (text) => { const m = (text ?? input).trim(); if (m) { setInput(""); send(m); } };
  const handleTicket = async () => {
    setTicketing(true);
    try { const r = await toTicket(); setOpen(false); navigate("/ask", { state: { fromChat: r.data } }); }
    catch { toast.error("Couldn't prepare the ticket — please try again"); }
    finally { setTicketing(false); }
  };
  const canTicket = !busy && msgs.some((m) => m.role === "assistant" && m.content);

  return (
    <>
      <button onClick={() => setOpen((v) => !v)} data-testid="chat-toggle-btn" aria-label="Chat with Dr.Gadget assistant"
        className="fixed bottom-20 md:bottom-6 right-3 md:right-4 z-50 w-12 h-12 md:w-14 md:h-14 rounded-full bg-cyan-500 text-slate-900 shadow-[0_0_24px_rgba(6,182,212,0.45)] grid place-items-center hover:scale-105 transition-transform">
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {open && (
        <div data-testid="chat-panel" className="fixed z-50 bottom-36 md:bottom-24 right-4 left-4 sm:left-auto sm:w-[380px] max-h-[70vh] flex flex-col rounded-2xl border border-slate-700 bg-[#0B1120]/95 backdrop-blur-xl shadow-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-800 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-cyan-400" />
            <div>
              <div className="font-display font-bold text-sm">Dr.Gadget Assistant</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">AI · 24/7 · free</div>
            </div>
          </div>
          <ChatMessages msgs={msgs} onSuggestion={submit} endRef={endRef} />
          {canTicket && (
            <div className="px-3 pb-2 -mt-1">
              <button onClick={handleTicket} disabled={ticketing} data-testid="chat-to-ticket-btn"
                className="w-full rounded-lg border border-cyan-500/40 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-200 text-xs font-mono uppercase tracking-widest py-2 flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                {ticketing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LifeBuoy className="w-3.5 h-3.5" />}
                {ticketing ? "Preparing ticket…" : "Still stuck? Send this to David"}
              </button>
            </div>
          )}
          <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="p-3 border-t border-slate-800 flex gap-2">
            <input className="dg-input flex-1" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Describe your problem…" data-testid="chat-input" disabled={busy} />
            <VoiceButton testId="chat-voice-btn" className="!px-2" onText={(t) => submit(t)} />
            <button type="submit" disabled={busy || !input.trim()} className="dg-btn-primary rounded-lg px-3 disabled:opacity-50" data-testid="chat-send-btn" aria-label="Send"><Send className="w-4 h-4" /></button>
          </form>
        </div>
      )}
    </>
  );
}
